import { forwardRef, ComponentType, PropsWithChildren, useId } from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";

import { VariantProps } from "class-variance-authority";
import {
  progressIndicatorVariants,
  progressTextVariants,
  progressVariants,
} from "../helpers/progress";

type PrimitiveProgressProps = React.ComponentPropsWithoutRef<
  typeof ProgressPrimitive.Root
>;

export type ProgressProps = Omit<PrimitiveProgressProps, "id"> &
  VariantProps<typeof progressVariants> &
  PropsWithChildren;

export type ProgressKind = NonNullable<
  VariantProps<typeof progressVariants>["kind"]
>;

const Progress = forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(({ className, value, kind, children, ...props }, ref) => {
  kind = kind ?? "info";
  value = value || 0;
  const id = useId();
  const progress = (
    <ProgressPrimitive.Root
      ref={ref}
      id={id}
      className={progressVariants({ kind, className })}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className={progressIndicatorVariants({ kind })}
        style={{ transform: `translateX(-${100 - value}%)` }}
      />
    </ProgressPrimitive.Root>
  );

  if (children) {
    return (
      <>
        {progress}
        <label htmlFor={id} className={progressTextVariants({ kind })}>
          <span>{children}</span>
          <span className="font-mono">{`${value}%`}</span>
        </label>
      </>
    );
  } else {
    return progress;
  }
});

(Progress as ComponentType).displayName = ProgressPrimitive.Root.displayName;

export { Progress };
